import React from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
} from '@lognai/synapse-design-system-v2';
import type { AiBundle, AiRiskBand, AiBundleStatus } from '@lognai/plugin-ai-core/src/types/bundle';
import type { AiDraftItem } from '@lognai/plugin-ai-core/src/types/draft-item';
import { useAiBundle } from '../../hooks/use-bundle';
import { WorkflowProgressCard } from '../WorkflowProgressCard';
import { deriveStepsFromDrafts } from '../../hooks/use-workflow-run';

type BadgeTone = 'neutral' | 'success' | 'warning' | 'destructive' | 'info';

export interface DraftBundleCardProps {
  bundleId: string;
  onReview?: (bundleId: string) => void;
  onViewHistory?: (bundleId: string) => void;
}

const RISK_TONE: Record<AiRiskBand, BadgeTone> = {
  low: 'info',
  medium: 'warning',
  high: 'destructive',
};

const RISK_LABEL: Record<AiRiskBand, string> = {
  low: 'Low risk',
  medium: 'Medium risk',
  high: 'High risk',
};

const STATUS_LABEL: Record<AiBundleStatus, string> = {
  pending: 'Awaiting review',
  applied: 'Applied',
  partially_applied: 'Partially applied',
  partially_applied_with_errors: 'Partially applied (with errors)',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

const isFinal = (status: AiBundleStatus): boolean =>
  status === 'applied' || status === 'partially_applied' || status === 'partially_applied_with_errors' || status === 'rejected' || status === 'cancelled';

// A bundle is "paused" when at least one step failed mid-run and the user must
// resolve it before the rest can proceed.
const isPaused = (status: AiBundleStatus): boolean =>
  status === 'partially_applied_with_errors';

// Order the bundle's items into step order. `draftIndex` is the canonical
// per-bundle ordinal (1-based, set at create time); fall back to createdAt.
const sortItems = (items: AiDraftItem[]): AiDraftItem[] =>
  [...items].sort((a, b) => {
    const ai = a.draftIndex ?? Number.MAX_SAFE_INTEGER;
    const bi = b.draftIndex ?? Number.MAX_SAFE_INTEGER;
    if (ai !== bi) return ai - bi;
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

export const DraftBundleCard: React.FC<DraftBundleCardProps> = ({ bundleId, onReview, onViewHistory }) => {
  const { data, isPending, isError } = useAiBundle(bundleId);

  if (isPending) {
    return (
      <Card style={{ marginTop: 8 }}>
        <CardContent>
          <div style={{ fontSize: 13, color: 'var(--synapse-text-muted, #6b7280)' }}>Loading…</div>
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card style={{ marginTop: 8 }}>
        <CardContent>
          <div style={{ fontSize: 13, color: 'var(--synapse-text-muted, #6b7280)' }}>Not available.</div>
        </CardContent>
      </Card>
    );
  }

  const bundle: AiBundle = data.bundle;
  const items = data.items ?? [];
  const final = isFinal(bundle.status);
  const paused = isPaused(bundle.status);

  // Action affordance: pending → Review; paused (errors) → Resolve; otherwise
  // (applied / partial / rejected / cancelled) → View history.
  const actionButton = paused ? (
    <Button variant="primary" size="sm" onClick={() => onReview?.(bundleId)}>Resolve</Button>
  ) : final ? (
    <Button variant="ghost" size="sm" onClick={() => onViewHistory?.(bundleId)}>View history</Button>
  ) : (
    <Button variant="primary" size="sm" onClick={() => onReview?.(bundleId)}>Review</Button>
  );

  // ── Multi-step (chained) bundle → render a numbered step list. We derive the
  // steps from the bundle's own items (no workflowRunId needed), so the chat
  // reads as "a sequence of steps", not "N unrelated drafts".
  if (items.length > 1) {
    const ordered = sortItems(items);
    const { steps } = deriveStepsFromDrafts(ordered);
    const firstFailed = steps.find((s) => s.status === 'failed');
    const headline = paused && firstFailed
      ? `Paused — step ${firstFailed.index + 1} failed`
      : bundle.status === 'pending'
      ? 'Awaiting your review'
      : STATUS_LABEL[bundle.status];

    return (
      <Card style={{ marginTop: 8 }}>
        <CardContent>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <Badge tone={RISK_TONE[bundle.riskBandMax]}>{RISK_LABEL[bundle.riskBandMax]}</Badge>
              <span
                data-testid="bundle-step-headline"
                style={{ fontSize: 13, fontWeight: 600, color: paused ? 'var(--synapse-destructive, #b91c1c)' : 'inherit' }}
              >
                {headline}
              </span>
            </div>
            {actionButton}
          </div>
          <WorkflowProgressCard
            workflowRunId={bundleId}
            steps={steps}
            variant="expanded"
            hideHeader
          />
        </CardContent>
      </Card>
    );
  }

  // ── Single change → compact one-liner.
  const summary = `1 change · ${STATUS_LABEL[bundle.status]}`;
  return (
    <Card style={{ marginTop: 8 }}>
      <CardContent>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Badge tone={RISK_TONE[bundle.riskBandMax]}>{RISK_LABEL[bundle.riskBandMax]}</Badge>
            <span style={{ fontSize: 13, color: 'var(--synapse-text-muted, #6b7280)' }}>
              {summary}
            </span>
          </div>
          {actionButton}
        </div>
      </CardContent>
    </Card>
  );
};
